/**
 * WebAuthn Utility Functions
 *
 * Provides helper functions for WebAuthn/Passkey operations including:
 * - Browser support detection
 * - Device name detection
 * - Credential serialization/deserialization
 */

import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationCredentialJSON,
  AuthenticationCredentialJSON,
} from '@/types/security';

// ============================================================================
// Browser Support Detection
// ============================================================================

/**
 * Check if the browser supports WebAuthn
 * @returns true if WebAuthn is supported
 */
export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator.credentials !== 'undefined' &&
    typeof navigator.credentials.create !== 'undefined' &&
    typeof navigator.credentials.get !== 'undefined'
  );
}

/**
 * Check if the browser supports platform authenticators (Touch ID, Face ID, Windows Hello)
 * @returns Promise resolving to true if platform authenticators are available
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false;
  }

  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Check if conditional mediation (autofill) is supported
 * This allows passkey suggestions in form autofill
 * @returns Promise resolving to true if conditional mediation is supported
 */
export async function isConditionalMediationSupported(): Promise<boolean> {
  if (!isWebAuthnSupported()) {
    return false;
  }

  try {
    // Check if the browser supports conditional mediation
    if (typeof PublicKeyCredential.isConditionalMediationAvailable === 'function') {
      return await PublicKeyCredential.isConditionalMediationAvailable();
    }
    return false;
  } catch {
    return false;
  }
}

// ============================================================================
// Device Name Detection
// ============================================================================

/**
 * Auto-detect the device name based on user agent and platform
 * @returns A human-readable device name
 */
export function detectDeviceName(): string {
  const ua = navigator.userAgent;
  const platform = navigator.platform || '';

  // Check for specific devices first
  if (/iPhone/.test(ua)) {
    return 'iPhone';
  }
  if (/iPad/.test(ua)) {
    return 'iPad';
  }
  if (/Android/.test(ua)) {
    // Try to extract device model
    const match = ua.match(/Android[^;]*;\s*([^)]+)/);
    if (match?.[1]) {
      const model = match[1].trim();
      // Clean up common patterns
      if (model.includes('Build')) {
        const parts = model.split('Build');
        const firstPart = parts[0];
        return firstPart ? firstPart.trim() : 'Android Device';
      }
      return model.length > 30 ? 'Android Device' : model;
    }
    return 'Android Device';
  }

  // Check for desktop platforms
  if (/Macintosh|Mac OS X/.test(ua)) {
    return 'Mac';
  }
  if (/Windows/.test(ua)) {
    return 'Windows PC';
  }
  if (/Linux/.test(ua) && !/Android/.test(ua)) {
    return 'Linux PC';
  }
  if (/CrOS/.test(ua)) {
    return 'Chromebook';
  }

  // Check for security keys by looking at platform
  if (platform.toLowerCase().includes('win')) {
    return 'Windows PC';
  }
  if (platform.toLowerCase().includes('mac')) {
    return 'Mac';
  }

  // Fallback
  return 'Unknown Device';
}

/**
 * Get a more specific device name based on authenticator type
 * Can be called after registration to refine the device name
 * @param authenticatorAttachment The attachment type from the credential
 * @returns A refined device name
 */
export function refineDeviceName(
  authenticatorAttachment?: AuthenticatorAttachment | null
): string {
  const baseName = detectDeviceName();

  if (authenticatorAttachment === 'cross-platform') {
    // Likely a security key
    return 'Security Key';
  }

  return baseName;
}

// ============================================================================
// Base64URL Encoding/Decoding
// ============================================================================

/**
 * Convert an ArrayBuffer to a base64url-encoded string
 * @param buffer The ArrayBuffer to encode
 * @returns Base64url-encoded string
 */
export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    const byte = bytes[i];
    if (byte !== undefined) {
      binary += String.fromCharCode(byte);
    }
  }
  const base64 = btoa(binary);
  // Convert to base64url format
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Convert a base64url-encoded string to an ArrayBuffer
 * @param base64url The base64url-encoded string
 * @returns ArrayBuffer
 */
export function base64UrlToArrayBuffer(base64url: string): ArrayBuffer {
  // Convert base64url to base64
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding if necessary
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// ============================================================================
// Credential Options Parsing
// ============================================================================

/**
 * Parse server registration options into WebAuthn API format
 * Converts base64url strings to ArrayBuffers as required by the WebAuthn API
 * @param options The options from the server (JSON format)
 * @returns PublicKeyCredentialCreationOptions for navigator.credentials.create()
 */
export function parseCreationOptions(
  options: PublicKeyCredentialCreationOptionsJSON
): PublicKeyCredentialCreationOptions {
  const publicKey: PublicKeyCredentialCreationOptions = {
    rp: options.rp,
    user: {
      id: base64UrlToArrayBuffer(options.user.id),
      name: options.user.name,
      displayName: options.user.displayName,
    },
    challenge: base64UrlToArrayBuffer(options.challenge),
    pubKeyCredParams: options.pubKeyCredParams,
  };

  if (options.timeout !== undefined) {
    publicKey.timeout = options.timeout;
  }
  if (options.attestation) {
    publicKey.attestation = options.attestation;
  }
  if (options.authenticatorSelection) {
    publicKey.authenticatorSelection = options.authenticatorSelection;
  }
  if (options.extensions) {
    publicKey.extensions = options.extensions;
  }

  if (options.excludeCredentials) {
    publicKey.excludeCredentials = options.excludeCredentials.map((cred) => ({
      type: cred.type,
      id: base64UrlToArrayBuffer(cred.id),
      transports: cred.transports,
    }));
  }

  return publicKey;
}

/**
 * Parse server authentication options into WebAuthn API format
 * @param options The options from the server (JSON format)
 * @returns PublicKeyCredentialRequestOptions for navigator.credentials.get()
 */
export function parseRequestOptions(
  options: PublicKeyCredentialRequestOptionsJSON
): PublicKeyCredentialRequestOptions {
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: base64UrlToArrayBuffer(options.challenge),
    timeout: options.timeout,
    rpId: options.rpId,
    userVerification: options.userVerification,
    extensions: options.extensions,
  };

  if (options.allowCredentials) {
    publicKey.allowCredentials = options.allowCredentials.map((cred) => ({
      type: cred.type,
      id: base64UrlToArrayBuffer(cred.id),
      transports: cred.transports,
    }));
  }

  return publicKey;
}

// ============================================================================
// Credential Serialization
// ============================================================================

/**
 * Serialize a registration credential to JSON for sending to the server
 * Converts ArrayBuffers to base64url strings
 * @param credential The credential from navigator.credentials.create()
 * @returns JSON-serializable credential object
 */
export function serializeRegistrationCredential(
  credential: PublicKeyCredential
): RegistrationCredentialJSON {
  const response = credential.response as AuthenticatorAttestationResponse;

  // Get transports if available (requires casting to access getTransports)
  let transports: AuthenticatorTransport[] | undefined;
  if (typeof response.getTransports === 'function') {
    transports = response.getTransports() as AuthenticatorTransport[];
  }

  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type as 'public-key',
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      attestationObject: arrayBufferToBase64Url(response.attestationObject),
      transports,
    },
    authenticatorAttachment: credential.authenticatorAttachment as AuthenticatorAttachment | undefined,
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

/**
 * Serialize an authentication credential to JSON for sending to the server
 * @param credential The credential from navigator.credentials.get()
 * @returns JSON-serializable credential object
 */
export function serializeAuthenticationCredential(
  credential: PublicKeyCredential
): AuthenticationCredentialJSON {
  const response = credential.response as AuthenticatorAssertionResponse;

  return {
    id: credential.id,
    rawId: arrayBufferToBase64Url(credential.rawId),
    type: credential.type as 'public-key',
    response: {
      clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
      authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
      signature: arrayBufferToBase64Url(response.signature),
      userHandle: response.userHandle
        ? arrayBufferToBase64Url(response.userHandle)
        : undefined,
    },
    authenticatorAttachment: credential.authenticatorAttachment as AuthenticatorAttachment | undefined,
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Error types for WebAuthn operations
 */
export type WebAuthnErrorType =
  | 'not_supported'
  | 'cancelled'
  | 'timeout'
  | 'security_error'
  | 'invalid_state'
  | 'not_allowed'
  | 'constraint_error'
  | 'unknown';

/**
 * Custom error class for WebAuthn operations
 */
export class WebAuthnError extends Error {
  type: WebAuthnErrorType;

  constructor(type: WebAuthnErrorType, message: string) {
    super(message);
    this.name = 'WebAuthnError';
    this.type = type;
  }
}

/**
 * Get a user-friendly error message for WebAuthn errors
 * @param error The error from WebAuthn operations
 * @returns A user-friendly error message and type
 */
export function parseWebAuthnError(error: unknown): WebAuthnError {
  if (error instanceof DOMException) {
    switch (error.name) {
      case 'NotSupportedError':
        return new WebAuthnError(
          'not_supported',
          'Your browser or device does not support this authentication method.'
        );
      case 'AbortError':
        return new WebAuthnError(
          'cancelled',
          'Authentication was cancelled.'
        );
      case 'TimeoutError':
        return new WebAuthnError(
          'timeout',
          'Authentication timed out. Please try again.'
        );
      case 'SecurityError':
        return new WebAuthnError(
          'security_error',
          'A security error occurred. Please ensure you are using a secure connection.'
        );
      case 'InvalidStateError':
        return new WebAuthnError(
          'invalid_state',
          'This authenticator is already registered with your account.'
        );
      case 'NotAllowedError':
        return new WebAuthnError(
          'not_allowed',
          'Authentication was not allowed. Please try again.'
        );
      case 'ConstraintError':
        return new WebAuthnError(
          'constraint_error',
          'The authenticator does not meet the requirements.'
        );
      default:
        return new WebAuthnError(
          'unknown',
          error.message || 'An unknown error occurred.'
        );
    }
  }

  if (error instanceof Error) {
    return new WebAuthnError('unknown', error.message);
  }

  return new WebAuthnError('unknown', 'An unknown error occurred.');
}
